import { useSyncExternalStore } from "react";

import { randomUUID } from "~/lib/utils";
import { normalizeMorrowDicebearAvatarSeed } from "./morrowPersonAvatarCatalog";

export const MORROW_TEAM_STORAGE_KEY = "morrow:team:v1";

export type MorrowTeamMember = {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly role: string;
  readonly avatarSeed: string;
};

export type MorrowTeamMessage = {
  readonly id: string;
  readonly author: string;
  readonly text: string;
  readonly createdAt: string;
  readonly mentionsMor: boolean;
};

export type MorrowTeamState = {
  readonly members: ReadonlyArray<MorrowTeamMember>;
  readonly messages: ReadonlyArray<MorrowTeamMessage>;
};

const EMPTY_STATE: MorrowTeamState = {
  members: [],
  messages: [],
};

const listeners = new Set<() => void>();
let cachedState: MorrowTeamState | null = null;

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseMember(value: unknown): MorrowTeamMember | null {
  if (!isRecord(value)) return null;
  const id = typeof value.id === "string" ? value.id.trim() : "";
  const name = typeof value.name === "string" ? value.name.trim() : "";
  const title = typeof value.title === "string" ? value.title.trim() : "";
  const role = typeof value.role === "string" ? value.role.trim() : "";
  if (!id || !name) return null;
  return {
    id,
    name,
    title,
    role,
    avatarSeed:
      normalizeMorrowDicebearAvatarSeed(
        typeof value.avatarSeed === "string"
          ? value.avatarSeed
          : typeof value.avatarCharacterId === "string"
            ? value.avatarCharacterId
            : "",
      ) ?? "",
  };
}

function parseMessage(value: unknown): MorrowTeamMessage | null {
  if (!isRecord(value)) return null;
  const id = typeof value.id === "string" ? value.id.trim() : "";
  const author = typeof value.author === "string" ? value.author.trim() : "";
  const text = typeof value.text === "string" ? value.text.trim() : "";
  const createdAt = typeof value.createdAt === "string" ? value.createdAt : "";
  if (!id || !author || !text || !createdAt) return null;
  return {
    id,
    author,
    text,
    createdAt,
    mentionsMor: value.mentionsMor === true || messageMentionsMor(text),
  };
}

function parseState(value: unknown): MorrowTeamState | null {
  if (!isRecord(value)) return null;
  const members = Array.isArray(value.members)
    ? value.members.flatMap((member) => {
        const parsed = parseMember(member);
        return parsed ? [parsed] : [];
      })
    : [];
  const messages = Array.isArray(value.messages)
    ? value.messages.flatMap((message) => {
        const parsed = parseMessage(message);
        return parsed ? [parsed] : [];
      })
    : [];
  return { members, messages };
}

function readState(): MorrowTeamState {
  const storage = safeStorage();
  if (!storage) return EMPTY_STATE;
  try {
    const raw = storage.getItem(MORROW_TEAM_STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    return parseState(JSON.parse(raw)) ?? EMPTY_STATE;
  } catch {
    return EMPTY_STATE;
  }
}

function emit() {
  for (const listener of listeners) listener();
}

function writeState(next: MorrowTeamState): MorrowTeamState {
  const storage = safeStorage();
  if (storage) {
    try {
      storage.setItem(MORROW_TEAM_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Best-effort; the in-memory snapshot still updates for this session.
    }
  }
  cachedState = next;
  emit();
  return next;
}

export function messageMentionsMor(text: string): boolean {
  return /(^|[\s])@mor\b/i.test(text);
}

export function getTeamState(): MorrowTeamState {
  return (cachedState ??= readState());
}

export function subscribeToTeamState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTeamState(): MorrowTeamState {
  return useSyncExternalStore(subscribeToTeamState, getTeamState, getTeamState);
}

function newId(prefix: string): string {
  return randomUUID();
}

export function addTeamMember(input: {
  readonly name: string;
  readonly title?: string;
  readonly role?: string;
  readonly avatarSeed?: string;
}): MorrowTeamMember | null {
  const name = input.name.trim();
  const title = input.title?.trim() ?? "";
  const role = input.role?.trim() ?? "";
  const avatarSeed = normalizeMorrowDicebearAvatarSeed(input.avatarSeed) ?? "";
  if (!name) return null;
  const member: MorrowTeamMember = { id: newId("member"), name, title, role, avatarSeed };
  const current = getTeamState();
  writeState({
    ...current,
    members: [...current.members, member],
  });
  return member;
}

export function joinTeam(input: {
  readonly id: string;
  readonly name: string;
  readonly title?: string;
  readonly role?: string;
  readonly avatarSeed?: string;
}): MorrowTeamState | null {
  const id = input.id.trim();
  const name = input.name.trim();
  const title = input.title?.trim() ?? "";
  const role = input.role?.trim() ?? "";
  const avatarSeed = normalizeMorrowDicebearAvatarSeed(input.avatarSeed) ?? "";
  if (!id || !name) return null;
  const current = getTeamState();
  if (current.members.some((member) => member.id === id)) return current;
  return writeState({
    ...current,
    members: [...current.members, { id, name, title, role, avatarSeed }],
  });
}

export function upsertTeamMember(input: {
  readonly id: string;
  readonly name: string;
  readonly title?: string;
  readonly role?: string;
  readonly avatarSeed?: string;
}): MorrowTeamState | null {
  const id = input.id.trim();
  const name = input.name.trim();
  const title = input.title?.trim() ?? "";
  const role = input.role?.trim() ?? "";
  const avatarSeed = normalizeMorrowDicebearAvatarSeed(input.avatarSeed) ?? "";
  if (!id || !name) return null;
  const current = getTeamState();
  const index = current.members.findIndex((member) => member.id === id);
  if (index < 0) {
    return writeState({
      ...current,
      members: [...current.members, { id, name, title, role, avatarSeed }],
    });
  }
  const members = current.members.slice();
  const existing = members[index];
  if (!existing) return current;
  if (
    existing.name === name &&
    existing.title === title &&
    existing.role === role &&
    existing.avatarSeed === avatarSeed
  ) {
    return current;
  }
  members[index] = { ...existing, name, title, role, avatarSeed };
  return writeState({ ...current, members });
}

export function postTeamMessage(input: {
  readonly author: string;
  readonly text: string;
}): MorrowTeamMessage | null {
  const author = input.author.trim();
  const text = input.text.trim();
  if (!author || !text) return null;
  const message: MorrowTeamMessage = {
    id: newId("msg"),
    author,
    text,
    createdAt: new Date().toISOString(),
    mentionsMor: messageMentionsMor(text),
  };
  const current = getTeamState();
  writeState({
    ...current,
    messages: [...current.messages, message],
  });
  return message;
}

export function resetTeamState(): void {
  const storage = safeStorage();
  if (storage) {
    try {
      storage.removeItem(MORROW_TEAM_STORAGE_KEY);
    } catch {
      // Best-effort.
    }
  }
  cachedState = EMPTY_STATE;
  emit();
}
